import { Election } from './domain/elections';
import { Parties, PvtVds, Version } from './domain/stuctures';
import { User } from './domain/user';

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

export function getDefaultStructure(user: User) {
  const {
    country,
    provinces,
    regions,
    constituencies,
    municipalities,
    wards,
    votingdistricts,
  } = user.geoRoles;

  if (country.length && country[0] === 'ZA') {
    return {
      type: 'country',
      code: 'ZA',
    };
  } else if (provinces.length) {
    return {
      type: 'province',
      code: provinces[0],
    };
  } else if (regions.length) {
    return {
      type: 'region',
      code: regions[0],
    };
  } else if (constituencies.length) {
    return {
      type: 'constituency',
      code: constituencies[0],
    };
  } else if (municipalities.length) {
    return {
      type: 'municipality',
      code: municipalities[0],
    };
  } else if (wards.length) {
    return {
      type: 'ward',
      code: wards[0],
    };
  } else if (votingdistricts?.length) {
    return {
      type: 'votingdistrict',
      code: votingdistricts[0],
    };
  } else {
    throw new Error('Not authorized to access this dashboard');
  }
}

// geoRoles hold province codes, but some data (e.g. the user report) holds full names.
// Alternative codes are included because sources disagree (GT/GP, KZN/KN, LP/NP).
export const PROVINCE_NAMES: Record<string, string> = {
  EC: 'Eastern Cape',
  FS: 'Free State',
  GT: 'Gauteng',
  GP: 'Gauteng',
  KZN: 'KwaZulu-Natal',
  KN: 'KwaZulu-Natal',
  LP: 'Limpopo',
  NP: 'Limpopo',
  MP: 'Mpumalanga',
  NC: 'Northern Cape',
  NW: 'North West',
  WC: 'Western Cape',
};

// Lowercase and drop spaces/hyphens so "KwaZulu Natal" matches "KwaZulu-Natal"
export const normaliseProvinceName = (name: string) =>
  name.toLowerCase().replace(/[\s-]/g, '');

// The user report is limited to national or provincial users.
// `provinces` holds normalised full names for matching against report rows.
export function getReportAccess(user?: User) {
  const national = Boolean(user?.geoRoles?.country?.includes('ZA'));
  const provinces = (user?.geoRoles?.provinces ?? [])
    .map(code => PROVINCE_NAMES[code.toUpperCase()] ?? code)
    .map(normaliseProvinceName);
  return {
    national,
    provinces,
    allowed: national || provinces.length > 0,
  };
}

export function capitalize(text: string): string {
  return String(text[0]).toUpperCase() + String(text).slice(1);
}

type PostJsonOptions = {
  headers?: Record<string, string>;
};

export async function postJson(
  url: string,
  body: unknown,
  options?: PostJsonOptions
): Promise<Response> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers ?? {}),
    },
    body: JSON.stringify(body),
  });

  if (response.ok) {
    return await response;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `Request failed (${response.status})`);
  }
}

export const getBallotVersion = (vd: PvtVds, version: number): Version[] => {
  if (!vd?.versions) return [];
  return vd.versions.filter(v => v.version === version);
};

export const prevVersionCount = (
  versions: Version[],
  type: string,
  version: number,
  parties: Parties
): number | undefined => {
  const prevVersion = versions.find(v => v.version === version - 1);
  if (!prevVersion) return;
  const ballot = prevVersion.ballots.find(v => v.type === type);
  const party = ballot?.parties.find(p => p.party === parties.party);
  return party?.count;
};

// Total IEC count (summed across all parties) for the same ballot type in the
// previous version. Returns undefined when there is no previous version/ballot.
// Used to suppress trend arrows when the previous ballot had no IEC results yet.
export const prevVersionBallotTotal = (
  versions: Version[],
  type: string,
  version: number
): number | undefined => {
  const prevVersion = versions.find(v => v.version === version - 1);
  if (!prevVersion) return;
  const ballot = prevVersion.ballots.find(v => v.type === type);
  if (!ballot) return;
  return ballot.parties.reduce((acc, p) => acc + (p.count ?? 0), 0);
};

export const ELECTION_STORAGE_KEY = 'pvt.selectedElectionId';

// localStorage key for the selected election's start date (YYYY-MM-DD)
export const ELECTION_STORAGE_DATE = 'pvt.selectedElectionStartDate';

// Cuts an ISO date string down to just the date part, e.g.
// "2026-10-17T00:00:00.000Z" -> "2026-10-17". Slicing the string (instead of
// going through Date/moment) avoids the day shifting because of timezones.
export const toDateOnly = (iso: string | undefined): string | undefined =>
  iso ? iso.slice(0, 10) : undefined;

// Stored election if it still exists, otherwise the first one
export const pickElection = (
  elections: Election[],
  preferredId: string | undefined
): Election | undefined =>
  elections.find(e => e.id === preferredId) ?? elections[0];

// Election id used by the issues sync service (different id system from getElections)
export const SYNC_ELECTION_ID = '6ae03c3515b5340b808850e5';
